import { z } from 'zod';
import { ExtraServiceSchema } from '~entities/extra-service/model/extra-service.contracts';
import { InsuranceOfferSchema } from '~entities/insurance-offer/model/insurance-offer.contracts';
import { PriceSchema } from '~entities/price/model/price.contracts';
import { UserSchema } from '~entities/user/model/user.contracts';

export const LastBookingItemSchema = z.object({
  packageGroupId: z.number().optional(),
  price: PriceSchema,
  ratePlanId: z.number(),
  roomCategoryId: z.number(),
  roomCategoryName: z.string(),
});

export const LastBookingSchema = z.object({
  bookingId: z.string(),
  depositAmount: PriceSchema,
  id: z.number(),
  items: z.array(LastBookingItemSchema),
  status: z.string(),
  total: PriceSchema,
});

export const CartItemSchema = z.object({
  id: z.string(),
  type: z.enum(['ROOM', 'LAST_MINUTE_PACKAGE', 'PACKAGE']),
  arrivalDay: z.string(),
  nights: z.number(),
  guests: z.array(
    z.object({
      id: z.string(),
      type: z.enum(['CHILD', 'ADULT']),
      childAge: z.number().int().optional(),
      birthdate: z.string().optional(),
      firstname: z.string().optional(),
      lastname: z.string().optional(),
    }),
  ),

  price: PriceSchema,
  comment: z.string().optional(),
  overlayMessages: z.array(z.string()),
  bookingMessages: z.array(z.string()),
  // False when a price policy allows only an inquiry for the selected rate (samera/samera#3217).
  bookable: z.boolean().optional(),
  alternativeRates: z.array(
    z.object({
      rateId: z.number().int(),
      price: z.object({
        amount: z.number(),
        currency: z.string(),
        symbol: z.string(),
      }),
      overlayMessages: z.array(z.string()),
      bookingMessages: z.array(z.string()),
      bookable: z.boolean().optional(),
    }),
  ),
  extraServices: z.array(ExtraServiceSchema).optional(),

  rateId: z.number().int(),
  roomId: z.number().int(),
  lastMinutePackageId: z.number().int().optional(),
  packageId: z.number().int().optional(),
  packageCategoryId: z.number().int().optional(),
  // Self-contained package display data, so the checkout no longer depends on the package being
  // present in the (filtered) configuration. (samera/samera#3468)
  packageDetails: z
    .object({
      id: z.number().int(),
      name: z.string(),
      includedServicesHtml: z.string(),
    })
    .nullish(),
});

export const SessionSchema = z.object({
  sessionId: z.string(),
  user: UserSchema,
  erv: z
    .object({
      offers: z.array(InsuranceOfferSchema),
    })
    .partial()
    .optional(),
  cart: z.array(CartItemSchema),
  total: PriceSchema,
  deposit: z.object({
    depositPercentage: z.number(),
    depositAmount: PriceSchema,
  }),
  promotionCode: z.string().optional(),
});

export const UpdateCartItemSchema = CartItemSchema.partial().extend({
  id: CartItemSchema.shape.id,
  guests: z.array(CartItemSchema.shape.guests.element.partial()),
});
